<template>
  <div class="modal-overlay" v-if="visible" @click.self="close">
    <div class="modal-card">
      <div class="modal-header">
        <h3>{{ fund ? fund.name : '基金操作' }}</h3>
        <span class="close-btn" @click="close">✕</span>
      </div>
      
      <div class="modal-body" v-if="fund">
        <div class="info-card">
          <div class="info-row">
            <label>当前持仓</label>
            <span class="value">¥ {{ formatMoney(fund.holding) }}</span>
          </div>
          <div class="divider"></div>
          <div class="info-row">
            <label>最新净值</label>
            <span class="value">{{ fund.netValue }} <small :class="fund.dailyRate >= 0 ? 'red' : 'green'">({{ fund.dailyRate }}%)</small></span>
          </div>
        </div>

        <div class="action-section">
          <div class="input-wrapper">
            <span class="currency-symbol">¥</span>
            <input 
              v-model.number="amount" 
              type="number" 
              placeholder="0.00" 
              class="amount-input"
            />
          </div>
          
          <div class="btn-group">
            <button class="btn add-btn" @click="addHolding" :disabled="!amount || amount <= 0">
              <span class="icon">+</span> 加仓
            </button>
            <button class="btn reduce-btn" @click="reduceHolding" :disabled="!amount || amount <= 0">
              <span class="icon">−</span> 减仓
            </button>
          </div>
          
          <transition name="fade">
            <p class="hint" v-if="amount > 0">
              变更后预估: <span class="highlight">¥ {{ formatMoney(previewHolding) }}</span>
            </p>
          </transition>
        </div>
      </div>

      <div class="modal-footer" v-if="fund">
        <button class="btn delete-btn" @click="confirmDelete">删除此基金</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FundActionModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    fund: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      amount: ''
    }
  },
  computed: {
    previewHolding() {
      // 仅用于展示，不做实际计算逻辑
      // 这里无法区分是加还是减，只能根据最后点击的按钮决定
      // 实际上可以在点击按钮时直接提交
      return this.fund ? this.fund.holding : 0; 
    }
  },
  methods: {
    close() {
      this.amount = '';
      this.$emit('close');
    },
    formatMoney(value) {
      if (value == null) return '0.00';
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    addHolding() {
      if (!this.amount || this.amount <= 0) return;
      this.$emit('update', { 
        ...this.fund, 
        holding: this.fund.holding + this.amount 
      });
      this.close();
    },
    reduceHolding() {
      if (!this.amount || this.amount <= 0) return;
      let newHolding = this.fund.holding - this.amount;
      if (newHolding < 0) newHolding = 0; // 防止负资产
      this.$emit('update', { 
        ...this.fund, 
        holding: newHolding
      });
      this.close();
    },
    confirmDelete() {
      if (confirm(`确定要删除 "${this.fund.name}" 吗？`)) {
        this.$emit('delete', this.fund.code);
        this.close();
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
  animation: fadeIn 0.2s ease-out;
}

.modal-card {
  background: white;
  width: 100%;
  max-width: 360px;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: zoomIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f1f1f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

.close-btn {
  font-size: 20px;
  color: #999;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #e6e6e6;
  color: #666;
}

.info-card {
  background: #f7f8fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.divider {
  height: 1px;
  background: rgba(0,0,0,0.06);
  margin: 12px 0;
}

.info-row label {
  color: #8c8c8c;
}

.info-row .value {
  font-weight: 600;
  color: #333;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.action-section {
  margin-bottom: 20px;
}

.input-wrapper {
  position: relative;
  margin-bottom: 16px;
}

.currency-symbol {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: #333;
  font-weight: 600;
}

.amount-input {
  width: 100%;
  padding: 16px 16px 16px 36px;
  border: 2px solid #f0f0f0;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 600;
  color: #333;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
  background: #fff;
}

.amount-input:focus {
  border-color: #1890ff;
}

.amount-input::placeholder {
  color: #d9d9d9;
}

.btn-group {
  display: flex;
  gap: 12px;
}

.btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: transform 0.1s, opacity 0.2s;
}

.btn:active {
  transform: scale(0.98);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.add-btn {
  background: linear-gradient(135deg, #ff7875 0%, #f5222d 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(245, 34, 45, 0.3);
}

.reduce-btn {
  background: linear-gradient(135deg, #95de64 0%, #52c41a 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(82, 196, 26, 0.3);
}

.hint {
  font-size: 13px;
  color: #999;
  margin-top: 12px;
  text-align: center;
}

.highlight {
  color: #333;
  font-weight: 600;
}

.modal-footer {
  margin-top: auto;
  border-top: 1px solid #f0f0f0;
  padding-top: 20px;
}

.delete-btn {
  width: 100%;
  background-color: #fff;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
  padding: 10px;
  border-radius: 12px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.delete-btn:hover {
  background-color: #fff1f0;
  border-color: #ff4d4f;
}

.red { color: #ff4d4f; }
.green { color: #52c41a; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
